<template>
  <page-template>
    <template #title>Progressions</template>
    <template #type>
      <app-drop-down-menu setting="type"
                          :options="scales"
                          map-by="n"
                          text="Type: "
                          :currently-selected="scale.n"
                          @updated="setState"
                          :small="true"/>
    </template>
    <template #search>
      <!--                <search @search="searchScales" @termChange="termChange"/>-->
    </template>
    <template #other-settings>
      <v-expansion-panels focusable popout class="py-3 px-2">
        <v-expansion-panel>
          <v-expansion-panel-header ripple class="py-0">
            <div class="font-weight-bold caption">
              {{ getHarmony.name }},
              {{ getTransportBPM }}bpm
              {{ loop ? ', Loop' : '' }}
              {{ getIsMetronomeActive ? ', Metronome' : '' }}, {{ getLineName }}
              <v-spacer/>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card>
              <v-container fluid>
                <v-row align="center" justify="center">
                  <select-music-line @line-changed="updateProgressions" class="pt-0"/>
                </v-row>
                <v-row no-gutters align="center" justify="center" class="text--center">
                  <v-col :cols="12" class="pt-8">
                    <app-slider value-name="BPM" :initial-value="getTransportBPM" :min="40" :max="200"
                                @valueChanged="setBPM"/>
                  </v-col>
                  <v-col class="justify-center">
                    <span class="body-1">Loop</span>
                    <v-switch v-model="loop"
                              color="success"
                              class="ma-0 pa-0"/>
                  </v-col>
                  <v-col>
                    <span class="body-1">Metronome</span>
                    <v-switch v-model="metronome"
                              color="success"
                              class="ma-0 pa-0"/>
                  </v-col>
                  <v-col>
                    <v-row>
                      <v-col :cols="12">
                        <span class="body-1">Sound</span>
                      </v-col>
                      <v-col>
                        <v-menu offset-y>
                          <template #activator="{ on }">
                            <v-btn small rounded outlined class="body-0"
                                   v-on="on">
                              {{ getMetronomeSound }}
                            </v-btn>
                          </template>
                          <v-list>
                            <v-list-item v-for="(sound, index) in drumSounds"
                                         :key="index"
                                         @click="updateMetronomeSound(sound)">
                              <v-list-item-title>{{ sound }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
    <template #content>
      <v-expansion-panels>
        <progression-card v-for="(progression, i) in pagination.data()"
                          :key="progressionCardKeys[i]"
                          :progression="progression"
                          :scale="scaleInstance"
                          :index="i + (pagination.pageStart)"
                          :currently-active="activeProgression"
                          @setCurrentActive="setCurrentActive"/>
      </v-expansion-panels>
    </template>
    <template #bottom>
      <div style="display: block">
        <span class="v-card pa-3 body-2 mt-4">Want me to add your progressions and harmonies? Email me at seandvir12@gmail.com ! They will be
          up in no time and you will be credited :)</span>
      </div>
    </template>
    <template #pagination>
      <v-pagination circle color="primary" v-model="pagination.page"
                    :length="pagination.pages"/>
    </template>
    <template #help-title>Help</template>
    <template #help-text>
      Here you can browse different chord progressions, click on them to see information about their chords.
      <br><br>
      You can change the root note and the scale used to generate the progressions.
      <br><br>
      You can also change the tempo, the harmony being played on the chord and the music notes being used to play it.
      <br><br>
      You can also play a metronome and loop the progression to just try and improvise over it if you'd like :)
    </template>
  </page-template>
</template>

<script>
  import {mapGetters, mapActions} from 'vuex'
  import Paginator       from 'vue-paginator3k'
  import scales          from 'assets/json/common-scale-ref'
  import progressions    from 'assets/json/progressions-ref'
  import {drumSounds} from 'misc/Constants'
  import audioManager from 'models/AudioManager'
  import { createScale } from 'helpers/note-art-helpers'

  export default {
    name: 'Progression',

    head: {
      title: function () {
        return {
          inner: 'Progressions',
        }
      },
      meta:  [
        {
          name:    'music theory chord progressions',
          content: 'an interactive list of music chord progressions to explore and play',
          id:      'desc',
        },
      ],
    },

    components: {
      PageTemplate:    () => import(/* webpackChunkName: "components" */ 'components/PageTemplate'),
      ProgressionCard: () => import(/* webpackChunkName: "feature-sections" */ 'sections/app/cards/ProgressionCard'),
      AppDropDownMenu: () => import(/* webpackChunkName: "components" */ 'components/AppDropDownMenu'),
      SelectMusicLine: () => import(/* webpackChunkName: "feature-sections" */ 'sections/app/SelectMusicLine'),
      AppSlider:       () => import(/* webpackChunkName: "components" */ 'components/AppSlider'),
    },

    async created() {
      await audioManager.add('drums')
      await this.updateProgressions()
      this.pagination.data(progressions)
    },

    data() {
      return {
        scales,
        drumSounds,
        scale:               scales[0],
        progressionCardKeys: [],
        pagination:          new Paginator(10),
        activeProgression:   null,
      }
    },
    computed: {
      ...mapGetters('transport', [
        'getIsMetronomeActive',
        'getMetronomeSound',
        'getIsTransportLooping',
        'getIsTransportStarted',
        'getTransportBPM',
      ]),
      ...mapGetters('context', ['getPitch', 'getHarmony', 'getLineName']),
      scaleInstance: function () {
        const {n, p} = this.scale
        return createScale(this.getPitch, p, {name: n})
      },
      metronome:     {
        set() {
          this.toggleMetronome()
        },
        get() {
          return this.getIsMetronomeActive
        },
      },
      loop:          {
        set(val) {
          this.updateMasterLoop(val)
        },
        get() {
          return this.getIsTransportLooping
        },
      },
    },

    methods: {
      ...mapActions('transport', [
        'toggleMetronome',
        'resetTransport',
        'setBPM',
        'updateMasterLoop',
        'setMetronomeSound',
      ]),
      updateProgressions() {
        this.resetDriver()
        const rand = Math.floor(Math.random() * 100)
        const len  = progressions.length
        for (let i = 0; i < len; ++i) {
          this.progressionCardKeys[i] = rand + i
        }
      },

      updateMetronomeSound(sound) {
        this.setMetronomeSound({sound})
      },

      resetDriver() {
        if (this.getIsTransportStarted) {
          this.resetTransport()
        }
      },

      setState({value}) {
        this.scale = value
        this.updateProgressions()
        this.resetTransport()
      },

      setCurrentActive(name) {
        this.activeProgression = name
      },
    },
  }
</script>

<style>
  .v-input__slot {
    align-items:     center;
    justify-content: center;
  }
</style>
